import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { DayPicker } from "react-day-picker";

import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { DayButtonProps } from "react-day-picker";

// react-day-picker marks every day in a selected range as `selected` *in
// addition to* its specific range_start/middle/end modifier, so styling via
// classNames (CSS cascade) is non-deterministic — two independently-toggled
// utility classes can both set `background`, and which one wins depends on
// Tailwind's generated stylesheet order, not markup order. Picking the style
// with plain JS conditionals instead makes exactly one branch apply.
function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: DayButtonProps) {
  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString()}
      className={cn(
        buttonVariants({ variant: "ghost" }),
        "size-9 p-0 font-normal",
        modifiers.today && !modifiers.selected && "bg-accent",
        modifiers.range_middle && "rounded-none bg-accent",
        (modifiers.range_start || modifiers.range_end) &&
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
        modifiers.range_start && "rounded-l-md",
        modifiers.range_end && "rounded-r-md",
        modifiers.selected &&
          !modifiers.range_start &&
          !modifiers.range_middle &&
          !modifiers.range_end &&
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
        modifiers.outside && "text-muted-foreground opacity-50",
        modifiers.disabled && "text-muted-foreground opacity-50",
        className,
      )}
      {...props}
    />
  );
}

function Calendar({
  className,
  classNames,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      className={cn("p-3", className)}
      classNames={{
        months: "flex gap-4",
        month: "flex flex-col gap-3",
        month_caption: "flex justify-center pt-1 relative items-center h-9",
        caption_label: "text-sm font-medium",
        nav: "flex items-center justify-between absolute inset-x-0 top-0 h-9",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "size-7 bg-transparent p-0",
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "size-7 bg-transparent p-0",
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday:
          "text-muted-foreground w-9 font-normal text-[0.8rem] text-center",
        week: "flex w-full mt-2",
        day: "size-9 text-center p-0 relative",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeft className="size-4" />
          ) : (
            <ChevronRight className="size-4" />
          ),
        DayButton: CalendarDayButton,
      }}
      {...props}
    />
  );
}

export { Calendar };
